/**
 * Risk Classification (structural risk — ORDIN 1761) constants.
 * Shared by nomenclature, assignments, and Risk-Driven scheduling.
 * Do not mix with procedural RPN / location risk profile.
 */

/** Score factor values per PRD (PO-SSPLIAAM) */
export const PC_VALUES = [
  { value: 1, labelKey: 'riskClassification.pcLow' },
  { value: 2, labelKey: 'riskClassification.pcMedium' },
  { value: 3, labelKey: 'riskClassification.pcHigh' }
];

export const FA_VALUES = [
  { value: 1, labelKey: 'riskClassification.faLow' },
  { value: 3, labelKey: 'riskClassification.faHigh' }
];

export const S_VALUES = [
  { value: 0, labelKey: 'riskClassification.sLow' },
  { value: 1, labelKey: 'riskClassification.sHigh' }
];

/** Min/max total score */
export const RISK_SCORE_MIN = 2;
export const RISK_SCORE_MAX = 7;

/**
 * Frequency buckets — platform-level, shared by nomenclature default frequency
 * and autocontrol frequency on entities. No fixed time of day.
 */
// labelKey: use short key; component uses useTranslation('riskClassification') so t('freqContinuous') resolves
export const RISK_CLASSIFICATION_FREQUENCY_BUCKETS = [
  { code: 'CONTINUOUS', labelKey: 'freqContinuous', displayOrder: 1 },
  { code: 'MULTIPLE_DAILY', labelKey: 'freqMultipleDaily', displayOrder: 2 },
  { code: 'ONCE_DAILY', labelKey: 'freqOnceDaily', displayOrder: 3 },
  { code: 'EVERY_2_DAYS', labelKey: 'freqEvery2Days', displayOrder: 4 },
  { code: 'WEEKLY', labelKey: 'freqWeekly', displayOrder: 5 },
  { code: 'EVERY_2_WEEKS', labelKey: 'freqEvery2Weeks', displayOrder: 6 },
  { code: 'MONTHLY', labelKey: 'freqMonthly', displayOrder: 7 },
  { code: 'EVERY_2_MONTHS', labelKey: 'freqEvery2Months', displayOrder: 8 },
  { code: 'EVERY_3_MONTHS', labelKey: 'freqEvery3Months', displayOrder: 9 },
  { code: 'EVERY_6_MONTHS', labelKey: 'freqEvery6Months', displayOrder: 10 },
  { code: 'YEARLY', labelKey: 'freqYearly', displayOrder: 11 }
];

/** Default nomenclature (ORDIN 1761 aligned). One doc per org; this is the seed. */
export const DEFAULT_RISK_CLASSIFICATION_LEVELS = [
  {
    id: 'high',
    label: 'High Risk',
    color: '#dc2626',
    scoreMin: 7,
    scoreMax: 7,
    defaultFrequencyCode: 'MULTIPLE_DAILY',
    displayOrder: 1
  },
  {
    id: 'medium-high',
    label: 'Medium-High Risk',
    color: '#ea580c',
    scoreMin: 5,
    scoreMax: 6,
    defaultFrequencyCode: 'ONCE_DAILY',
    displayOrder: 2
  },
  {
    id: 'medium',
    label: 'Medium Risk',
    color: '#ca8a04',
    scoreMin: 4,
    scoreMax: 4,
    defaultFrequencyCode: 'EVERY_2_DAYS',
    displayOrder: 3
  },
  {
    id: 'low',
    label: 'Low Risk',
    color: '#16a34a',
    scoreMin: 2,
    scoreMax: 3,
    defaultFrequencyCode: 'WEEKLY',
    displayOrder: 4
  }
];

/**
 * Resolve criticality level from total score using levels array (score ranges must be gap-free).
 * @param {number} totalScore - PC + FA + S (2–7)
 * @param {Array<{ scoreMin, scoreMax, id, label, color, defaultFrequencyCode }>} levels
 * @returns {object|null} level or null if no match
 */
export function getLevelForScore(totalScore, levels) {
  if (!levels || levels.length === 0) return null;
  const score = Number(totalScore);
  const level = levels
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .find(l => score >= l.scoreMin && score <= l.scoreMax);
  return level || null;
}

/**
 * Validate that levels cover 2–7 with no gaps and no overlaps.
 * @param {Array<{ scoreMin, scoreMax }>} levels
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateScoreRanges(levels) {
  if (!levels || levels.length === 0) {
    return { valid: false, error: 'At least one level is required' };
  }
  const min = RISK_SCORE_MIN;
  const max = RISK_SCORE_MAX;
  const sorted = levels
    .map(l => ({ scoreMin: Number(l.scoreMin), scoreMax: Number(l.scoreMax) }))
    .filter(l => !Number.isNaN(l.scoreMin) && !Number.isNaN(l.scoreMax))
    .sort((a, b) => a.scoreMin - b.scoreMin);
  let lastMax = min - 1;
  for (const l of sorted) {
    if (l.scoreMin > l.scoreMax) {
      return { valid: false, error: `Level has scoreMin (${l.scoreMin}) > scoreMax (${l.scoreMax})` };
    }
    if (l.scoreMin !== lastMax + 1) {
      return { valid: false, error: `Gap or overlap: expected next scoreMin ${lastMax + 1}, got ${l.scoreMin}` };
    }
    lastMax = l.scoreMax;
  }
  if (lastMax !== max) {
    return { valid: false, error: `Score range must end at ${max}, got ${lastMax}` };
  }
  return { valid: true };
}
